import { gql, useMutation, useQuery } from '@apollo/client';
import { Callout, Flex, SegmentedControl } from '@radix-ui/themes';
import { CircleDashed, Clock } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { use, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { SortPreferenceContext } from '../../SortPreferenceContext';
import { FeatureFlags } from '../../constants/featureFlags';
import { postStats, useStats } from '../../hooks/useStats';
import { APPLICATION_SUMMARY_FRAGMENT } from '../ApplicationFragments';
import { HubertTooltip } from '../HubertTooltip';
import { getStatusObject } from '../common';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import Title from '../elements/Title';
import JobHeader from '../jobHeader/JobHeader';
import ActionButtonBar from '../swiper/ActionButtonBar';
import ApplicantSelector, { type ScrollableDivRef } from '../swiper/ApplicantSelector';
import Header, { type Activity, type Application } from '../swiper/Header';
import AnswerFilterButton from '../swiper/AnswerFilterButton';
import AnswerFilterPanel from '../swiper/AnswerFilterPanel';
import type { AnswerOptionSelection } from '../swiper/useAnswerFilter';
import { trackAnswerFilter } from '../swiper/useAnswerFilter';
import Summary from '../swiper/Summary';
import { Error } from './Error';

const makeApplicantSelectorQuery = (answers: { variable: string; argument: string }) => gql`
  query JOB_QUERY(
    $jobId: String
    $limit: Int
    $offset: Int
    $hidePassed: Boolean
    $stage: Int
    $sort: ApplicationSortableFields!
    ${answers.variable}
  ) {
    job(jobId: $jobId) {
      title
      id
      company
      integration {
        teamtailor {
          jobId
          companyId
        }
      }
      location {
        id
        name
        address
      }
      collaborators {
        users {
          user {
            id
            firstName
            lastName
            email
          }
        }
      }
      threshold
      new: applications(filter: { score: { gte: 0 }, stage: { equals: 2 }, accepted: { equals: null }, ${answers.argument} }) {
        totalCount
      }
      leads: applications(
        filter: { stage: { equals: 2 }, scoreGteThreshold: true, accepted: { equals: null }, qualified: { ne: false }, ${answers.argument} }
      ) {
        totalCount
      }
      accepted: applications(filter: { stage: { equals: 3 }, ${answers.argument} }) {
        totalCount
      }
      hired: applications(filter: { stage: { equals: 4 }, ${answers.argument} }) {
        totalCount
      }
      passed: applications(
        filter: { stage: { equals: 2 }, score: { gte: 0 }, accepted: { equals: null }, qualified: { ne: false }, ${answers.argument} }
      ) {
        totalCount
      }
      notQualified: applications(filter: { qualified: { equals: false }, ${answers.argument} }) {
        totalCount
      }
      manualRejected: applications(filter: { accepted: { equals: false }, ${answers.argument} }) {
        totalCount
      }
      total: applications {
        totalCount
      }
      allIds: applications(limit: 10000, filter: { stage: { equals: $stage }, ${answers.argument} }, sort: { field: $sort, order: DESC }) {
        totalCount
        nodes {
          id
          status
        }
      }
      applications(
        limit: $limit
        skip: $offset
        filter: { stage: { equals: $stage }, scoreGteThreshold: $hidePassed, ${answers.argument} }
        sort: { field: $sort, order: DESC }
      ) {
        totalCount
        nodes {
          id
          candidate {
            id
            firstName
            lastName
          }
          job {
            id
            threshold
          }
          score
          status
          score
          stage
          createdAt
          accepted
          activityLog {
            createdAt
            stage
            actionType
            actionSubType
            message
          }
          aiDetection {
            isAi
          }
        }
      }
    }
  }
`;

// Without the answer filter (PostHog flag answer-filter) the query doesn't mention answers, so every
// other client sends exactly the query it did before.
const APPLICANT_SELECTOR_QUERY = makeApplicantSelectorQuery({ variable: '', argument: '' });
const APPLICANT_SELECTOR_QUERY_WITH_ANSWERS = makeApplicantSelectorQuery({
  variable: '$answerOptions: [AnswerOptionFilter!]',
  argument: 'answerOptions: $answerOptions',
});

const SWIPER_QUERY = gql`
  query Application($applicationId: String) {
    application(applicationId: $applicationId) {
      id
      ...Application_summaries
      candidate {
        id
        firstName
        lastName
        email
        phoneNumber
      }
      openApplicationSummary {
        isDisabled
        expiry
      }
      score
      job {
        id
        title
        threshold
        integration {
          teamtailor {
            jobId
            companyId
          }
        }
      }
      integration {
        teamtailor {
          jobId
          candidateId
        }
      }
      status
      interviews {
        nodes {
          id
        }
      }
      stage
      createdAt
      accepted
      activityLog {
        createdAt
        status
        stage
        actionType
        actionSubType
        message
      }
      aiDetection {
        isAi
        score
        tag
      }
      documents {
        id
        type
      }
    }
  }
  ${APPLICATION_SUMMARY_FRAGMENT}
`;

const UPDATE_APPLICATION = gql`
  mutation UPDATE_APPLICATION($applicationId: String!, $accepted: Boolean, $newStatus: String) {
    applicationMutation(
      input: { applicationId: $applicationId, accepted: $accepted, newStatus: $newStatus, source: "swiper" }
    ) {
      id
      candidate {
        id
        firstName
        lastName
        email
      }
      score
      id
      job {
        id
        title
        threshold
      }
      status
      score
      stage
      createdAt
      accepted
      activityLog {
        createdAt
        status
        stage
        actionType
        actionSubType
        message
      }
      openApplicationSummary {
        isDisabled
        expiry
      }
      summaries {
        totalCount
        nodes {
          id
          threshold
          summaryPart: summary {
            header
            icon
            bonuspoints
            average
            points
            maxPoints
            threshold
            details {
              label
              question
              icon
              answer
              points
              bonuspoints
              isCorrect
              qualified
              options {
                label
                points
                isCorrect
                answered
                evaluation {
                  name
                  value
                  bonus_points
                }
              }
            }
          }
        }
      }
    }
  }
`;

const initState = {
  applicants: [],
  job: {
    id: undefined,
    title: undefined,
    total: undefined,
    location: undefined,
    teamtailor: { jobId: undefined, companyId: undefined },
  },
  stats: {
    leads: 0,
    passed: 0,
    notQualified: 0,
    accepted: 0,
    rejected: 0,
    applications: 0,
    hired: 0,
  },
  selected: 0,
  threshold: 0,
};

const getStageName = (stageNum: number) => {
  switch (stageNum) {
    case 2:
      return 'newLeads';
    case 3:
      return 'acceptedLeads';
    case 4:
      return 'hired';
    case 5:
      return 'notHired';
    default:
      return 'newApplicants';
  }
};

function reducer(
  this: { setFirst: (value: boolean) => void; navigate: ReturnType<typeof useNavigate> },
  state: {
    selected: number;
    threshold: number;
    job: {
      id?: string;
      title?: string;
      location?: unknown;
      teamtailor: { jobId?: string; companyId?: string };
      total?: number;
    };
    stats: {
      leads: number;
      passed: number;
      notQualified: number;
      accepted: number;
      rejected: number;
      applications: number;
      hired: number;
    };
    applicants: Application[];
  },
  action: {
    type: string;
    applicationId?: string;
    count?: number;
    status?: string;
    leads?: number;
    passed?: number;
    notQualified?: number;
    accepted?: number;
    rejected?: number;
    applications?: number;
    hired?: number;
    activityLog?: Activity[];
    applicants?: Application[];
    jobData?: {
      id: string;
      title: string;
      location: unknown;
      threshold: number;
      integration: { teamtailor: { jobId: string; companyId: string } };
      total: number;
    };
  },
) {
  const { setFirst, navigate } = this;
  const next = {
    ...state,
    selected: state.selected,
    applicants: [...state.applicants],
  };
  const tempIndex = next.applicants.findIndex((a) => a.id === action.applicationId);
  const numApplicants = next.applicants.length - 1;

  switch (action.type) {
    case 'setNewApplicants':
      next.applicants = action.applicants ?? [];
      next.selected = 0;
      setFirst(true);
      return next;
    case 'setApplicants':
      next.applicants = action.applicants ?? state.applicants;
      setFirst(false);
      return next;
    case 'setJobInfo':
      if (action.jobData) {
        next.threshold = action.jobData.threshold;
        next.job.id = action.jobData.id;
        next.job.title = action.jobData.title;
        next.job.location = action.jobData.location;
        next.job.teamtailor.jobId = action.jobData.integration.teamtailor.jobId;
        next.job.teamtailor.companyId = action.jobData.integration.teamtailor.companyId;
      }
      return next;
    case 'setCount':
      next.job.total = action.count;
      return next;
    case 'updateStats':
      next.stats.leads = action.leads ?? 0;
      next.stats.passed = action.passed ?? 0;
      next.stats.notQualified = action.notQualified ?? 0;
      next.stats.accepted = action.accepted ?? 0;
      next.stats.rejected = action.rejected ?? 0;
      next.stats.applications = action.applications ?? 0;
      next.stats.hired = action.hired ?? 0;

      return next;
    case 'candidate-accept': {
      if (next.applicants[tempIndex].accepted === null) {
        next.stats.accepted++;
      } else if (next.applicants[tempIndex].accepted === false) {
        next.stats.accepted++;
        next.stats.rejected--;
      }

      next.applicants[tempIndex] = {
        ...next.applicants[tempIndex],
        stage: 3,
        accepted: true,
      };
      next.applicants[tempIndex].status = 'no_next';

      next.selected = next.selected + 1;
      if (next.selected > numApplicants) next.selected = numApplicants;

      const selected = next.applicants[next.selected];
      navigate('/job/' + selected.job.id + '/' + selected.id);

      return next;
    }
    case 'candidate-reject': {
      if (next.applicants[tempIndex].accepted === null) {
        next.stats.rejected++;
      } else if (next.applicants[tempIndex].accepted === true) {
        next.stats.accepted--;
        next.stats.rejected++;
      }

      next.applicants[tempIndex] = {
        ...next.applicants[tempIndex],
        stage: 5,
        accepted: false,
      };
      next.applicants[tempIndex].status = 'manually_rejected';

      next.selected = next.selected + 1;
      if (next.selected > numApplicants) next.selected = numApplicants;

      const selected = next.applicants[next.selected];
      navigate('/job/' + selected.job.id + '/' + selected.id);

      return next;
    }
    case 'select':
      if (tempIndex === -1) {
        next.selected = 0;
        return next;
      } else if (action !== null) {
        next.selected = tempIndex;

        const selected = next.applicants[tempIndex];
        const path = '/job/' + selected.job.id + '/' + selected.id;
        if (path !== window.location.pathname) navigate(path);

        return next;
      } else return next;
    case 'next': {
      next.selected = next.selected + 1;
      if (next.selected > numApplicants) next.selected = numApplicants;

      const selected = next.applicants[next.selected];
      navigate('/job/' + selected.job.id + '/' + selected.id);

      return next;
    }
    case 'prev': {
      next.selected = next.selected - 1;
      if (next.selected < 0) next.selected = 0;

      const selected = next.applicants[next.selected];
      navigate('/job/' + selected.job.id + '/' + selected.id);

      return next;
    }
    case 'status': {
      const statusObject = getStatusObject(action.status ?? '');

      if (statusObject && 'accepted' in statusObject) {
        next.applicants[state.selected] = {
          ...next.applicants[state.selected],
          accepted: statusObject.accepted ?? false,
        };
      }

      if (statusObject && 'stage' in statusObject) {
        next.applicants[state.selected] = {
          ...next.applicants[state.selected],
          stage: statusObject.stage,
        };
      }

      next.applicants[state.selected] = {
        ...next.applicants[state.selected],
        status: action.status ?? '',
      };

      next.selected = next.selected + 1;
      if (next.selected > numApplicants) next.selected = numApplicants;
      if (next.selected < 0) next.selected = 0;

      const selected = next.applicants[next.selected];
      navigate('/job/' + selected.job.id + '/' + selected.id);

      return next;
    }
    case 'activityLog':
      if (tempIndex !== -1) {
        next.applicants[state.selected] = {
          ...next.applicants[state.selected],
          activityLog: action.activityLog ?? [],
        };
      }

      return next;
    case 'loadMore':
      next.applicants = action.applicants
        ? [...state.applicants, ...action.applicants].filter(
            (applicant, i, self) => i === self.findIndex((t) => t.id === applicant.id),
          )
        : state.applicants;
      setFirst(false);
      return next;
    default:
      return state;
  }
}
const makeReducer = (args: ThisParameterType<typeof reducer>) => reducer.bind({ ...args });

const Swiper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appId, qtn } = useParams<{ appId: string; qtn?: string }>();

  const [initialApplicationId, setInitialApplicationId] = useState<string | undefined>();
  const [applicationStage, setApplicationStage] = useState<string>();
  const [limit] = useState(10);
  const [first, setFirst] = useState(true);
  const [secLoad, setSecLoad] = useState(false);
  const [hidePassed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sort, setSort] = use(SortPreferenceContext)!;

  const [state, dispatch] = useReducer(makeReducer({ setFirst, navigate }), initState);

  const applicantSelector = useRef<ScrollableDivRef>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const applicantInfoQuery = gql`
    query APPLICATION_INFO($applicationId: String) {
      application(applicationId: $applicationId) {
        id
        stage
        job {
          id
        }
      }
    }
  `;

  const applicantVars = {
    applicationId: initialApplicationId,
  };

  const { data: applicationInfoData } = useQuery(applicantInfoQuery, {
    variables: applicantVars,
    skip: initialApplicationId ? false : true,
  });

  // The PostHog flag decides which companies (clientId) or users get the answer filter.
  const answerFilterOn = useFeatureFlagEnabled(FeatureFlags.ANSWER_FILTER) ?? false;
  const [answerSelection, setAnswerSelection] = useState<AnswerOptionSelection[]>([]);
  // Filter mode: the answer filter takes the place of the candidate profile while the list keeps updating.
  const [answerPanelOpen, setAnswerPanelOpen] = useState(false);
  // The panel's ticks are a draft until Done (or the filter icon) closes it; then the list reloads once.
  const [answerDraft, setAnswerDraft] = useState<AnswerOptionSelection[]>([]);
  const answers = answerSelection.length > 0 ? answerSelection : null;
  const answersKey = JSON.stringify(answers);

  const vars = {
    jobId: applicationInfoData?.application?.job?.id,
    limit: parseInt(qtn ?? '0') > limit ? parseInt(qtn ?? '0') + 1 : limit,
    offset: 0,
    hidePassed: hidePassed,
    stage: applicationInfoData?.application?.stage ?? null,
    sort,
    ...(answerFilterOn && { answerOptions: answers }),
  };

  const [updateApplication, { data: updatedApplication }] = useMutation(UPDATE_APPLICATION, {
    onError: () => {
      reloadKanban();
    },
    onCompleted: (data) => {
      console.log(data);
    },
  });

  const skipApplicantsFetch = appId && !applicationInfoData && first ? true : false;
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch: reloadKanban,
  } = useQuery(answerFilterOn ? APPLICANT_SELECTOR_QUERY_WITH_ANSWERS : APPLICANT_SELECTOR_QUERY, {
    variables: vars,
    fetchPolicy: 'cache-and-network',
    skip: skipApplicantsFetch,
  });

  const { data: selectedApplicationData, error: selectedError } = useQuery(SWIPER_QUERY, {
    variables: {
      applicationId: appId,
    },
    fetchPolicy: 'cache-and-network',
    skip: skipApplicantsFetch || appId === undefined,
  });

  useEffect(() => {
    if (!vars.jobId) return;
    reloadKanban({ sort }).then((data) => {
      dispatch({ type: 'setNewApplicants', applicants: data.data.job.applications.nodes });
    });
  }, [sort]);

  // Ticks belong to one job: clear them when the recruiter moves to another job.
  const filterJobRef = useRef(vars.jobId);
  useEffect(() => {
    if (filterJobRef.current !== vars.jobId) {
      filterJobRef.current = vars.jobId;
      setAnswerSelection([]);
      setAnswerPanelOpen(false);
      setAnswerDraft([]);
    }
  }, [vars.jobId]);

  // A new answer filter restarts the list; move off the open candidate if it no longer matches. answerOptions is in
  // the query variables, so Apollo already fetches the filtered list by itself; wait for that result instead of
  // refetching, so one Done sends one request.
  const answersLoadedRef = useRef(answersKey);
  const pendingAnswersRef = useRef<string | null>(null);
  useEffect(() => {
    if (!vars.jobId || answersLoadedRef.current === answersKey) return;
    answersLoadedRef.current = answersKey;
    pendingAnswersRef.current = answersKey;
  }, [answersKey]);

  useEffect(() => {
    if (pendingAnswersRef.current !== answersKey || loading || !data?.job) return;
    pendingAnswersRef.current = null;
    const nodes = data.job.applications.nodes;
    // Did the filter help: how many candidates the filtered list holds (0 = a dead end).
    if (answers)
      track(AnalyticsEvents.ANSWER_FILTER_RESULT, {
        matches_in_list: data.job.applications.totalCount,
        question_count: answers.length,
        stage: vars.stage ?? 'none',
      });
    dispatch({ type: 'setNewApplicants', applicants: nodes });
    if (nodes.length > 0 && !data.job.allIds.nodes.some((a: { id: string }) => a.id === appId))
      navigate(`/job/${vars.jobId}/${nodes[0].id}`);
  }, [data, loading, answersKey]);

  useEffect(() => {
    setInitialApplicationId(appId);
  }, []);

  useEffect(() => {
    if (state.applicants[state.selected]?.id !== appId) dispatch({ type: 'select', applicationId: appId });
  }, [appId]);

  useEffect(() => {
    if (first && data && data.job) {
      if (data.job.applications.nodes.length > 0)
        setApplicationStage(getStageName(data.job.applications.nodes[0].stage));
      dispatch({ type: 'setApplicants', applicants: data.job.applications.nodes });
      dispatch({ type: 'setJobInfo', jobData: data.job });
      dispatch({ type: 'setCount', count: data.job.total.totalCount });
      dispatch({
        type: 'updateStats',
        leads: data.job.leads.totalCount,
        passed: data.job.passed.totalCount - data.job.leads.totalCount,
        notQualified: data.job.notQualified.totalCount,
        accepted: data.job.accepted.totalCount,
        rejected: data.job.manualRejected.totalCount,
        applications: data.job.applications.totalCount,
        hired: data.job.applications.totalCount,
      });
    }
  }, [data, first]);

  /**
   * If wanted application ID is not visiblefind placment and get applicants in list.
   */
  useEffect(() => {
    if (!data || !appId || !selectedApplicationData) return;
    if (state && state.applicants[state.selected]?.id === appId) return;

    const indexPlacement = data.job.allIds.nodes.findIndex((a: { id: string }) => a.id === appId);
    const lastIndex = state.applicants.length - 1;
    // Filtered out by the answer filter, not missing.
    if (indexPlacement === -1 && answers) return;
    if (indexPlacement === -1)
      toast.info(t('swiper.page.applicationNotFound.toast'), {
        description: appId && t('swiper.page.applicationNotFound.description', { appId }),
      });
    else if (indexPlacement === lastIndex) {
      loadMore();
    } else if (indexPlacement > lastIndex) {
      setSecLoad(true);
      reloadKanban({ jobId: applicationInfoData.application.job.id, limit: indexPlacement + 5, offset: 0 })
        .then((data) => {
          if (data) dispatch({ type: 'setApplicants', applicants: data.data.job.applications.nodes });
        })
        .then(() => {
          dispatch({ type: 'select', applicationId: appId });
          setSecLoad(false);
          applicantSelector.current?.customScrollTo(appId);
        });
    }
  }, [first, data, appId, selectedApplicationData]);

  /**
   * Scroll to application
   */
  useEffect(() => {
    if (data && !first && !secLoad && state && state.applicants[state.selected]) {
      headerRef.current?.scrollIntoView();
      applicantSelector.current?.customScrollTo(state.applicants[state.selected].id);
    }
  }, [state, data]);

  /**
   *  Select application ID from URL
   */
  useEffect(() => {
    if (appId && first) dispatch({ type: 'select', applicationId: appId });
  }, [appId, data, first]);

  useEffect(() => {
    if (updatedApplication) {
      const newData = updatedApplication.applicationMutation;
      dispatch({
        type: 'activityLog',
        activityLog: newData.activityLog,
        applicationId: newData.id,
      });
    }
  }, [updatedApplication]);

  useStats({ action: 'view_summary' }, []);

  /**
   *  Load more applicants
   */
  const loadMore = () => {
    setLoadingMore(true);
    fetchMore({
      variables: {
        limit: limit,
        offset: state.applicants.length,
      },
    }).then((data) => {
      const moreData = data.data;
      if (moreData) dispatch({ type: 'loadMore', applicants: moreData.job.applications.nodes });

      setLoadingMore(false);
    });
  };

  console.log({ selectedApplicationData, selected: state.applicants[state.selected] });
  const allErrors = !error || !selectedError;
  const showAnswerPanel = answerPanelOpen && answerFilterOn && !!vars.jobId;
  // The same stage and threshold as the candidate list (the "applications" alias), so the panel counts that list.
  const answerListFilter = { stage: { equals: vars.stage }, scoreGteThreshold: hidePassed };
  const openAnswerPanel = () => {
    track(AnalyticsEvents.ANSWER_FILTER_OPENED, {
      stage: vars.stage ?? 'none',
      active_questions: answerSelection.length,
    });
    setAnswerDraft(answerSelection);
    setAnswerPanelOpen(true);
  };
  // `via` tells whether recruiters find Done or close with the filter icon; `changed` whether they applied anything.
  const closeAnswerPanel = (via: 'done' | 'icon') => {
    const changed = JSON.stringify(answerDraft) !== JSON.stringify(answerSelection);
    track(AnalyticsEvents.ANSWER_FILTER_CLOSED, {
      via,
      changed,
      question_count: answerDraft.length,
      selected_count: answerDraft.reduce((sum, x) => sum + x.optionIds.length, 0),
    });
    if (changed) {
      trackAnswerFilter(answerDraft);
      setAnswerSelection(answerDraft);
    }
    setAnswerPanelOpen(false);
  };
  const showActionButton =
    state.applicants &&
    state.applicants.length > 0 &&
    applicationStage !== 'newApplicants' &&
    state.applicants[state.selected]?.id === selectedApplicationData?.application?.id;
  return (
    <div>
      <Title
        title={
          allErrors
            ? data && data.job
              ? data.job.title
              : t('swiper.page.titleFallback')
            : t('swiper.page.titleFallback')
        }
      />
      {Error(error ?? selectedError, () => (
        <Callout.Root color="red" className="mt-4">
          <Callout.Text className="mb-0">{t('swiper.page.accessError')}</Callout.Text>
        </Callout.Root>
      ))}
      {allErrors && data && !data.job && (
        <Callout.Root color="red" className="mt-4">
          <Callout.Text className="mb-0">{t('swiper.page.notFoundWithId', { appId })}</Callout.Text>
        </Callout.Root>
      )}
      {allErrors && applicationInfoData && applicationInfoData.application && (
        <>
          <JobHeader jobId={applicationInfoData.application.job.id} />

          <div className="border-grey-top pt-4 swiper-wrap flex">
            <ApplicantSelector
              applicantsData={state}
              selected={state.applicants[state.selected]?.id}
              ref={applicantSelector}
              stats={state.stats}
              selectedCallback={(e) => dispatch(e)}
              fetchMoreFunction={loadMore}
              loadingMore={loadingMore}
              loading={data}
              category={applicationStage}
              extra={
                <Flex gap="2" align="center" wrap="wrap">
                  <SegmentedControl.Root className="border border-gray-200" value={sort} onValueChange={setSort}>
                    <SegmentedControl.Item value="score">
                      <HubertTooltip tooltip={t('swiper.page.sort.score')}>
                        <span className="score-pill-md dark-grey">1</span>
                      </HubertTooltip>
                    </SegmentedControl.Item>
                    <SegmentedControl.Item value="createdAt">
                      <HubertTooltip tooltip={t('swiper.page.sort.invitationDate')}>
                        <Clock />
                      </HubertTooltip>
                    </SegmentedControl.Item>
                    <SegmentedControl.Item value="lastActivityAt">
                      <HubertTooltip tooltip={t('swiper.page.sort.latestActivity')}>
                        <CircleDashed />
                      </HubertTooltip>
                    </SegmentedControl.Item>
                  </SegmentedControl.Root>
                  {answerFilterOn && vars.jobId && (
                    <AnswerFilterButton
                      jobId={vars.jobId}
                      listFilter={answerListFilter}
                      selected={answerSelection}
                      onChange={setAnswerSelection}
                      open={answerPanelOpen}
                      onToggle={answerPanelOpen ? () => closeAnswerPanel('icon') : openAnswerPanel}
                    />
                  )}
                </Flex>
              }
            />

            {showAnswerPanel && (
              <AnswerFilterPanel
                jobId={vars.jobId}
                listFilter={answerListFilter}
                selected={answerDraft}
                onChange={setAnswerDraft}
                onClose={() => closeAnswerPanel('done')}
              />
            )}
            {/* Hidden, not unmounted, while filtering: the profile comes back without reloading. */}
            <div className={showAnswerPanel ? 'hidden' : 'swiper-body'}>
              {state.applicants && state.applicants.length > 0 && !secLoad && selectedApplicationData && (
                <>
                  <Header
                    application={selectedApplicationData.application}
                    ref={headerRef}
                    onStatusChange={(e) => {
                      if (e.type === 'status-execute') {
                        track(AnalyticsEvents.CANDIDATE_STATUS_CHANGED, { to_status: e.status, from: 'screening' });
                        updateApplication({
                          variables: {
                            source: 'swiper',
                            applicationId: selectedApplicationData.application.id,
                            newStatus: e.status,
                          },
                        });

                        dispatch({
                          type: 'status',
                          status: e.status,
                          applicationId: selectedApplicationData.application.id,
                        });
                      }
                    }}
                  />
                  <Summary
                    className={'swiper-actionbar-offset'}
                    summaries={selectedApplicationData.application.summaries.nodes}
                  />
                </>
              )}
              {(!data || secLoad) && (
                <Callout.Root color="blue">
                  <Callout.Text className="mb-0">{t('swiper.page.loading')}</Callout.Text>
                </Callout.Root>
              )}
              {data && state.applicants && state.applicants.length === 0 && !answers && (
                <small>{t('swiper.page.noMoreLeads')}</small>
              )}
              {data && state.applicants && state.applicants.length === 0 && answers && (
                <Callout.Root color="gray">
                  <Callout.Text className="mb-0">{t('swiper.page.answerFilter.noMatches')}</Callout.Text>
                </Callout.Root>
              )}
            </div>
            {!showAnswerPanel && showActionButton && (
              <ActionButtonBar
                actionCallback={(e) => {
                  if (e.type === 'up-execute') {
                    updateApplication({
                      variables: {
                        applicationId: e.applicationId,
                        accepted: true,
                        source: 'swiper',
                      },
                    });
                    postStats({ action: 'thumbs_up', applicationId: e.applicationId });
                    track(AnalyticsEvents.CANDIDATE_RATED, { rating: 'up' });
                    dispatch({ applicationId: e.applicationId, type: 'candidate-accept' });
                  } else if (e.type === 'down-execute') {
                    updateApplication({
                      variables: {
                        applicationId: e.applicationId,
                        accepted: false,
                        source: 'swiper',
                      },
                    });
                    postStats({ action: 'thumbs_down', applicationId: e.applicationId });
                    track(AnalyticsEvents.CANDIDATE_RATED, { rating: 'down' });
                    dispatch({ applicationId: e.applicationId, type: 'candidate-reject' });
                  } else {
                    if (e.type === 'prev' || e.type === 'next') {
                      track(AnalyticsEvents.CANDIDATE_NAVIGATED, { direction: e.type });
                    }
                    dispatch(e);
                  }
                }}
                applicantData={state.applicants[state.selected]}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Swiper;
